import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type {
  WorkRecordDto,
  WorkRecordCreateInput,
  WorkRecordUpdateInput,
  WorkRecordListQuery,
  WorkRecordListResponse,
  WorkRecordFacetsResponse,
  WorkPlanFaptReportResponse,
  WorkAnexa78Response,
  WorkAnexa1Response,
  WorkReportQuery,
} from '@forest/contracts';

export type WorksListParams = Partial<WorkRecordListQuery>;

function toSearchParams(q: WorksListParams): URLSearchParams {
  const p = new URLSearchParams();
  if (q.page !== undefined) p.set('page', String(q.page));
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.search) p.set('search', q.search);
  // CSV params — use !== undefined so an empty string (null sentinel) is sent.
  if (q.entitatiSilvice !== undefined) p.set('entitatiSilvice', q.entitatiSilvice);
  if (q.ocoaleSilvice !== undefined) p.set('ocoaleSilvice', q.ocoaleSilvice);
  if (q.raioane !== undefined) p.set('raioane', q.raioane);
  if (q.years !== undefined) p.set('years', q.years);
  if (q.months !== undefined) p.set('months', q.months);
  if (q.quarters !== undefined) p.set('quarters', q.quarters);
  if (q.workTypes !== undefined) p.set('workTypes', q.workTypes);
  if (q.fundingSources !== undefined) p.set('fundingSources', q.fundingSources);
  if (q.planFapt) p.set('planFapt', q.planFapt);
  // The "works for this polygon" banner sets parcelId; without this line the
  // request went out unfiltered and the banner was decorative.
  if (q.parcelId) p.set('parcelId', q.parcelId);
  if (q.includeArchived) p.set('includeArchived', 'true');
  if (q.includeDeleted) p.set('includeDeleted', 'true');
  if (q.sort) p.set('sort', q.sort);
  if (q.order) p.set('order', q.order);
  return p;
}

export function useWorks(params: WorksListParams, opts?: { enabled?: boolean }) {
  return useQuery<WorkRecordListResponse>({
    queryKey: ['works', params],
    queryFn: async () =>
      api.get('works', { searchParams: toSearchParams(params) }).json<WorkRecordListResponse>(),
    placeholderData: (prev) => prev,
    enabled: opts?.enabled ?? true,
  });
}

export function useWorkFacets() {
  return useQuery<WorkRecordFacetsResponse>({
    queryKey: ['works', 'facets'],
    queryFn: async () => api.get('works/facets').json<WorkRecordFacetsResponse>(),
    staleTime: 5 * 60_000,
  });
}

export function useCreateWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkRecordCreateInput) =>
      api.post('works', { json: input }).json<WorkRecordDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['works'] }),
  });
}

export function useUpdateWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: WorkRecordUpdateInput }) =>
      api.patch(`works/${id}`, { json: input }).json<WorkRecordDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['works'] }),
  });
}

export function useDeleteWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`works/${id}`).then(() => undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['works'] }),
  });
}

export function useRestoreWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`works/${id}/restore`).json<WorkRecordDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['works'] }),
  });
}

export function useArchiveWorkYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) =>
      api.post('works/archive-year', { json: { year } }).json<{ archived: number; year: number }>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['works'] }),
  });
}

function reportParams(q: Partial<WorkReportQuery>): URLSearchParams {
  const p = new URLSearchParams();
  if (q.year !== undefined) p.set('year', String(q.year));
  if (q.entitateSilvica) p.set('entitateSilvica', q.entitateSilvica);
  if (q.ocolSilvic) p.set('ocolSilvic', q.ocolSilvic);
  if (q.quarter !== undefined) p.set('quarter', String(q.quarter));
  if (q.planFapt) p.set('planFapt', q.planFapt);
  if (q.includeArchived) p.set('includeArchived', 'true');
  return p;
}

export function usePlanFaptReport(q: Partial<WorkReportQuery>, enabled = true) {
  return useQuery<WorkPlanFaptReportResponse>({
    queryKey: ['works', 'report', 'plan-fapt', q],
    queryFn: async () =>
      api.get('works/reports/plan-fapt', { searchParams: reportParams(q) }).json<WorkPlanFaptReportResponse>(),
    enabled,
  });
}

export function useAnexa78Report(q: Partial<WorkReportQuery>, enabled = true) {
  return useQuery<WorkAnexa78Response>({
    queryKey: ['works', 'report', 'anexa-78', q],
    queryFn: async () =>
      api.get('works/reports/anexa-78', { searchParams: reportParams(q) }).json<WorkAnexa78Response>(),
    enabled,
  });
}

export function useAnexa1Report(q: Partial<WorkReportQuery>, enabled = true) {
  return useQuery<WorkAnexa1Response>({
    queryKey: ['works', 'report', 'anexa-1', q],
    queryFn: async () =>
      api.get('works/reports/anexa-1', { searchParams: reportParams(q) }).json<WorkAnexa1Response>(),
    enabled,
  });
}
